import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
    TextField,
    Stack, 
    StackItem, 
    Text,
    ITextFieldStyles,
} from '@fluentui/react';
import ButtonPrimaryComponent from '../../components/button-primary/button-primary.component';
import ErrorMessageComponent from '../../components/error-message/error-message.component';
import LoaderComponent from '../../components/loader/loader.component';
import SelectSpaceComponent from '../../components/select-space/select-space.component';

import useAuthStatus from '../../hooks/auth/AuthHook';

import ItemUsuario from '../../models/ItemUsuario';
import ItemCliente from '../../models/ItemCliente';

const textFieldStyles : ITextFieldStyles = {
    root: {},
    fieldGroup: {
        borderColor: 'var(--color-primario)',
        borderRadius: 5,
        selectors: {
            "&:hover, &:focus, &:active": {
                borderColor: 'var(--color-primario)',
                borderRadius: 5,
            },
        },
    },
    prefix: {},
    suffix: {},
    field: {},
    icon: {},
    description: {},
    wrapper: {},
    errorMessage: {},
    subComponentStyles: {
        label: {},
    },
    revealButton: {},
    revealSpan: {},
    revealIcon: {},
};

interface ILoginPageProps {
    onLogin: (user: ItemUsuario) => void;
};

const LoginPage = (props: ILoginPageProps) => {
    const auth = useAuthStatus();
    
    const [error, setError] = useState<string>('');
    const [user, setUser] = useState<ItemUsuario>(new ItemUsuario());
    const [loading, setLoading] = useState<boolean>(false);

    const onChangeUser = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) : void => {
        setUser({ 
            ...user, 
            IdUsuario: newValue ? newValue : "",
        });
    };

    const onChangePassword = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) : void => {
        setUser({ 
            ...user, 
            Clave: newValue ? newValue : "",
        });
    };

    const onKeyUp = (event: React.KeyboardEvent) : void => {
        if (event.key === "Enter") {
            onLogin();
        }
    };

    const onLogin = () : void => {
        setError('');
        if (user.IdUsuario === "" || user.Clave === "") {
            setError("Alguno de los campos introducidos no es válido.");
        }
        else {
            setLoading(true);
            auth.login(user).then((response: ItemCliente[]) => {
                if (response.length === 1) {
                    onSelectCustomer(response[0]);
                }
                else {
                    setUser({
                        ...user,
                        Clientes: response,
                    });
                    setLoading(false);
                }
            }, (error: string) => {
                setError(error);
                setLoading(false);
            });
        }
    };

    const onSelectCustomer = (customer: ItemCliente) : void => {
        setUser({
            ...user,
            CodigoCliente: customer.CodigoCliente,
        });
    };

    const onLoginProps = props.onLogin;

    useEffect(() => {
        if (user.CodigoCliente) {
            setLoading(true);
            setError('');
            auth.selectCustomer(user).then((response: ItemUsuario) => {
                response.Clientes = user.Clientes;
                onLoginProps(response);
                setLoading(false);
            }, (error: string) => {
                setError(error);
                setLoading(false);
            });
        }

        return () => {
            setError('');
            setUser(new ItemUsuario());
            setLoading(false);
        };
    }, [user.CodigoCliente, onLoginProps]);

    return (
        <>
            <Stack>
                <StackItem className='mb-3'>
                    <Text 
                        className='fw-bold'
                        variant='large'>
                        Iniciar sesión:
                    </Text>
                </StackItem>
                <StackItem className='mb-3'>
                    <TextField
                        type="email"
                        autoComplete="on"
                        onChange={onChangeUser}
                        defaultValue={user.IdUsuario}
                        label="Correo electrónico"
                        styles={textFieldStyles}
                        onKeyUp={onKeyUp} />
                </StackItem>
                <StackItem className='mb-3'>
                    <TextField
                        type="password"
                        autoComplete="on"
                        canRevealPassword={false}
                        onChange={onChangePassword}
                        defaultValue={user.Clave}
                        label="Contraseña"
                        styles={textFieldStyles}
                        onKeyUp={onKeyUp} />
                </StackItem>
                <StackItem className='mb-3'>
                    <Stack 
                        horizontal={true}
                        className="d-flex align-items-center justify-content-between">
                        <StackItem>
                            <ButtonPrimaryComponent 
                                className='text-uppercase'
                                text='Iniciar sesión' 
                                onClick={onLogin} />
                        </StackItem>
                        <StackItem className='text-center flex-fill align-items-center'>
                            <Link 
                                to="/recuperar"
                                style={{
                                    color: 'var(--color-primario)',
                                    textDecoration: 'none',
                                }}>
                                ¿Olvidaste tu contraseña? Recúperala aquí
                            </Link>
                        </StackItem>
                    </Stack>
                </StackItem>
                <StackItem className='mb-3'>
                    <ErrorMessageComponent message={error} />
                </StackItem>
            </Stack>
            {
                (user.Clientes.length > 0) ? (
                    <SelectSpaceComponent 
                        customers={user.Clientes} 
                        onSelectCustomer={onSelectCustomer} />
                ) : (
                    null
                )
            }
            <LoaderComponent loading={loading} />
        </>
    );
};

export default LoginPage;
